import { JURISDICTIONS, type Jurisdiction } from './jurisdictions';
import { FAMILIES, type Family } from './families';
import { PLATE_SPECS, type PlateSpec } from './plateSpecs';

/**
 * The plate catalogue. Generated from `PLATE_SPECS` rather than hand-written, so
 * that rebalancing the whole game is a change to two small tables
 * (`jurisdictions.ts` weights and `families.ts` bases) instead of ~120 numbers.
 *
 * This is content, not state: it ships in the bundle, is never stored in D1 and
 * is never fetched at runtime, which is what keeps the app playable offline.
 */

/**
 * Physical plate shapes, in real millimetres. The pictogram draws each plate at
 * its true aspect ratio, so a Euro plate reads as long and low next to a
 * standard one without needing a label to say so.
 */
export type PlateShape = 'standard' | 'euro' | 'slimline' | 'motorcycle';

export const PLATE_SHAPES: Record<PlateShape, { w: number; h: number }> = {
  standard:   { w: 372, h: 134 },
  euro:       { w: 520, h: 110 },
  slimline:   { w: 372, h: 100 },
  motorcycle: { w: 255, h: 115 },
};

export type Rarity =
  | 'everyday' | 'common' | 'uncommon' | 'notable'
  | 'rare' | 'epic' | 'legendary' | 'mythic';

export interface PlateDef {
  id: string;
  name: string;
  family: Family;
  jurisdiction: Jurisdiction;
  points: number;
  rarity: Rarity;
  lookFor: string;
  colours: [string, string];
  /** Serial rendered on the mini plate pictogram. */
  sample: string;
  /** Legend rendered under the serial. */
  legend: string;
  shape: PlateShape;
  logo?: 'thylacine';
  verified: boolean;
}

/**
 * Bump when ids change meaning. Saved progress references ids, never indexes.
 *
 * Also bumped on a rebalance: scores are always derived from this table, so a
 * retune retroactively rescores every past trip, and a trip leaderboard caches
 * its scores server-side. The version is what tells the server those caches were
 * computed against an older catalogue - see `syncActiveTrip` in functions/api.
 *
 * 2: the balance pass - the hand-set `oneOff` values and the top of the family
 * ladder were compressed so a day's spotting outweighs one lucky exotic.
 *
 * 3: the platefix merge - hand-authored pictograms replace the procedural ones
 *    for six jurisdictions, plus 8 new plates and the Commonwealth and External
 *    corrections that came with them.
 */
export const CATALOGUE_VERSION = 3;

/**
 * Tier ceilings, exclusive. Rescaled with the balance pass: these are cut points
 * on the point curve, so compressing the curve without moving them would have
 * emptied the top three tiers and left one plate in `epic`. Chosen to keep the
 * distribution the tiers had before - a wide middle, a thin top - which the
 * "uses every rarity tier" test guards.
 */
const RARITY_THRESHOLDS: ReadonlyArray<readonly [number, Rarity]> = [
  [50, 'everyday'],
  [120, 'common'],
  [250, 'uncommon'],
  [450, 'notable'],
  [800, 'rare'],
  [1200, 'epic'],
  [1800, 'legendary'],
];

export function rarityFor(points: number): Rarity {
  for (const [ceiling, rarity] of RARITY_THRESHOLDS) {
    if (points < ceiling) return rarity;
  }
  return 'mythic';
}

export const RARITY_ORDER: Rarity[] = [
  'everyday', 'common', 'uncommon', 'notable', 'rare', 'epic', 'legendary', 'mythic',
];

/**
 * The id is also the pictogram filename (`src/pictograms/svg/{id}.svg`), so the
 * family segment's case is load-bearing: `oneOff` stays camelCase, giving ids like
 * `cth-oneOff-adf`. Lowercasing this would silently drop those pictograms on a
 * case-sensitive filesystem - the app would keep building and just fall back to the
 * procedural drawing. Saved progress references ids too, so a change here needs a
 * CATALOGUE_VERSION bump and a migration.
 */
function idFor(spec: PlateSpec): string {
  const base = `${spec.j.toLowerCase()}-${spec.f}`;
  return spec.slug ? `${base}-${spec.slug}` : base;
}

function nameFor(spec: PlateSpec): string {
  if (spec.name) return spec.name;
  return `${JURISDICTIONS[spec.j].short} ${FAMILIES[spec.f].nameSuffix}`.trim();
}

function pointsFor(spec: PlateSpec): number {
  // An explicit value wins outright, and every `oneOff` plate sets one. That
  // means the whole Commonwealth and External Territories set never touches a
  // jurisdiction weight - roughly a sixth of the catalogue's points are hand-set
  // here, not derived. Tuning JURISDICTIONS.EXT will not move them; the numbers
  // in plateSpecs.ts are the dial.
  if (spec.points !== undefined) return spec.points;
  if (spec.f === 'oneOff') {
    throw new Error(`Plate spec ${idFor(spec)} uses family "oneOff" but sets no explicit points.`);
  }
  return Math.round(FAMILIES[spec.f].base * JURISDICTIONS[spec.j].weight);
}

function shapeFor(spec: PlateSpec): PlateShape {
  if (spec.f === 'euro') return 'euro';
  if (spec.f === 'slimline') return 'slimline';
  if (spec.f === 'motorcycle') return 'motorcycle';
  return 'standard';
}


function build(spec: PlateSpec): PlateDef {
  const points = pointsFor(spec);
  return {
    id: idFor(spec),
    name: nameFor(spec),
    family: spec.f,
    jurisdiction: spec.j,
    points,
    rarity: rarityFor(points),
    lookFor: spec.lookFor,
    colours: spec.colours ?? JURISDICTIONS[spec.j].colours,
    sample: spec.sample ?? JURISDICTIONS[spec.j].sample,
    legend: spec.legend ?? JURISDICTIONS[spec.j].legend,
    shape: shapeFor(spec),
    ...(spec.logo ? { logo: spec.logo } : {}),
    verified: spec.verified,
  };
}

export const CATALOGUE: PlateDef[] = PLATE_SPECS.map(build);

export const CATALOGUE_BY_ID: ReadonlyMap<string, PlateDef> = new Map(
  CATALOGUE.map((p) => [p.id, p]),
);

/** Points for a complete collection - the denominator for "% collected". */
export const TOTAL_POINTS: number = CATALOGUE.reduce((sum, p) => sum + p.points, 0);

export function getPlate(id: string): PlateDef | undefined {
  return CATALOGUE_BY_ID.get(id);
}

/**
 * Structural checks that would otherwise fail confusingly at runtime. Exercised
 * by the unit tests rather than on every page load.
 */
export function validateCatalogue(): string[] {
  const problems: string[] = [];
  const seen = new Set<string>();

  for (const plate of CATALOGUE) {
    if (seen.has(plate.id)) problems.push(`Duplicate plate id: ${plate.id}`);
    seen.add(plate.id);

    if (!Number.isFinite(plate.points) || plate.points <= 0) {
      problems.push(`${plate.id} has non-positive points: ${plate.points}`);
    }
    if (!plate.lookFor.trim()) {
      problems.push(`${plate.id} has no lookFor text`);
    }
    if (!plate.sample.trim() || !plate.legend.trim()) {
      problems.push(`${plate.id} is missing pictogram text`);
    }
  }
  return problems;
}

/** Rows that still need checking against a transport-authority source. */
export function unverifiedPlates(): PlateDef[] {
  return CATALOGUE.filter((p) => !p.verified);
}
